/**
 * VP Carousel Style Renderers — bezpośrednie JSX renderery
 * Każdy styl (Noir Editorial, Data Driven, Bold Cards) ma 3 warianty:
 * cover, content, cta — mapowane automatycznie z ParsedSlide.
 */
import type { CSSProperties } from "react";
import type { ParsedSlide } from "../carousel-parser";

// ── Brand constants (VP brand book) ──────────────────

const VP_BRAND = {
  accent: "#E84C2B",
  accentLight: "#FF6B4A",
  accentGlow: "#E84C2B22",
  accentBorder: "#E84C2B44",
} as const;

const VP_FONT = "'Outfit', 'Plus Jakarta Sans', Inter, system-ui, sans-serif";

// ── Helpers ──────────────────────────────────────────

interface AccentPart {
  text: string;
  isAccent: boolean;
}

function parseAccent(text: string): AccentPart[] {
  const parts: AccentPart[] = [];
  const regex = /\{\{accent\|([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index), isAccent: false });
    }
    parts.push({ text: match[1], isAccent: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), isAccent: false });
  }
  return parts.length > 0 ? parts : [{ text, isAccent: false }];
}

function pageLabel(idx: number, total: number): string {
  return `${String(idx + 1).padStart(2, "0")}/${String(total).padStart(2, "0")}`;
}

type SlideTypeKey = "cover" | "content" | "cta";

function getSlideType(slide: ParsedSlide): SlideTypeKey {
  if (slide.isCover) return "cover";
  if (slide.isCTA) return "cta";
  return "content";
}

// ── Shared accent renderers ──────────────────────────

interface VpSlideProps {
  slide: ParsedSlide;
  slideIndex: number;
  totalSlides: number;
  profileHandle?: string;
}

function AccentTitle({ text, style }: { text: string; style: CSSProperties }) {
  const parts = parseAccent(text);
  return (
    <div style={style}>
      {parts.map((p, i) =>
        p.isAccent ? (
          <span key={i} style={{ color: VP_BRAND.accent }}>
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        )
      )}
    </div>
  );
}

function GradientAccentTitle({ text, style }: { text: string; style: CSSProperties }) {
  const parts = parseAccent(text);
  return (
    <div style={style}>
      {parts.map((p, i) =>
        p.isAccent ? (
          <span
            key={i}
            style={
              {
                background: `linear-gradient(90deg, ${VP_BRAND.accent}, ${VP_BRAND.accentLight})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              } as CSSProperties
            }
          >
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        )
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// NOIR EDITORIAL
// Magazynowy, typograficzny. Duża typografia, minimalne grafiki.
// ══════════════════════════════════════════════════════

function NoirCover({ slide, slideIndex, totalSlides, profileHandle }: VpSlideProps) {
  return (
    <div
      style={{
        background: "#0A0A0A",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "36px",
        fontFamily: VP_FONT,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "45%",
          height: "100%",
          background: "linear-gradient(180deg, #1C1412 0%, #0A0A0A 100%)",
          opacity: 0.6,
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          zIndex: 1,
        }}
      >
        <span
          style={{
            color: "#555",
            fontSize: "11px",
            letterSpacing: "3px",
            textTransform: "uppercase",
          }}
        >
          {profileHandle ? `@${profileHandle}` : "Viral Partners"}
        </span>
        <span style={{ color: "#555", fontSize: "11px", letterSpacing: "3px" }}>
          {pageLabel(slideIndex, totalSlides)}
        </span>
      </div>
      <div style={{ zIndex: 1 }}>
        <div
          style={{
            width: "40px",
            height: "3px",
            background: VP_BRAND.accent,
            marginBottom: "20px",
          }}
        />
        <AccentTitle
          text={slide.title || "Twój Nagłówek"}
          style={{
            color: "#fff",
            fontSize: "38px",
            fontWeight: 700,
            lineHeight: 1.05,
            letterSpacing: "-1px",
          }}
        />
        {slide.body && (
          <p
            style={{
              color: "#666",
              fontSize: "13px",
              marginTop: "16px",
              maxWidth: "280px",
              lineHeight: 1.5,
            }}
          >
            {slide.body}
          </p>
        )}
      </div>
    </div>
  );
}

function NoirContent({ slide, slideIndex, totalSlides }: VpSlideProps) {
  const pointNum = slideIndex;
  return (
    <div
      style={{
        background: "#0A0A0A",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "36px",
        fontFamily: VP_FONT,
        position: "relative",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "30px",
          right: "36px",
          color: "#333",
          fontSize: "11px",
          letterSpacing: "3px",
        }}
      >
        {pageLabel(slideIndex, totalSlides)}
      </span>
      <span
        style={{
          color: VP_BRAND.accent,
          fontSize: "72px",
          fontWeight: 800,
          lineHeight: 1,
          opacity: 0.12,
          position: "absolute",
          top: "20px",
          left: "36px",
        }}
      >
        {String(pointNum).padStart(2, "0")}
      </span>
      <div style={{ marginTop: "20px" }}>
        <AccentTitle
          text={slide.title || "Twój Nagłówek"}
          style={{
            color: "#fff",
            fontSize: "26px",
            fontWeight: 700,
            margin: "0 0 16px 0",
            lineHeight: 1.2,
          }}
        />
        <div style={{ width: "100%", height: "1px", background: "#1a1a1a", margin: "16px 0" }} />
        {slide.body && (
          <p
            style={{
              color: "#888",
              fontSize: "14px",
              lineHeight: 1.7,
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
          >
            {slide.body}
          </p>
        )}
      </div>
      <div
        style={{ position: "absolute", bottom: "30px", left: "36px", display: "flex", gap: "6px" }}
      >
        {Array.from({ length: Math.min(totalSlides, 10) }).map((_, i) => (
          <div
            key={i}
            style={{
              width: i === slideIndex ? "24px" : "6px",
              height: "3px",
              background: i === slideIndex ? VP_BRAND.accent : "#222",
              borderRadius: "2px",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function NoirCta({ slide }: VpSlideProps) {
  return (
    <div
      style={{
        background: "#0A0A0A",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "36px",
        fontFamily: VP_FONT,
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: "48px",
          height: "48px",
          border: `2px solid ${VP_BRAND.accent}`,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "24px",
        }}
      >
        <span style={{ color: VP_BRAND.accent, fontSize: "20px" }}>→</span>
      </div>
      <AccentTitle
        text={slide.title || "Działaj teraz"}
        style={{
          color: "#fff",
          fontSize: "28px",
          fontWeight: 700,
          margin: "0 0 12px 0",
          lineHeight: 1.2,
        }}
      />
      {slide.body && (
        <p style={{ color: "#666", fontSize: "13px", margin: "0 0 24px 0", lineHeight: 1.5 }}>
          {slide.body}
        </p>
      )}
      <div
        style={{
          padding: "10px 28px",
          background: VP_BRAND.accent,
          borderRadius: "4px",
          color: "#fff",
          fontSize: "13px",
          fontWeight: 600,
          letterSpacing: "1px",
          textTransform: "uppercase",
        }}
      >
        Link w bio
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// DATA DRIVEN
// Duże cyfry, metryki, dashboard feel. Buduje autorytet przez dane.
// ══════════════════════════════════════════════════════

function DataCover({ slide, slideIndex, totalSlides, profileHandle }: VpSlideProps) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #0D0D10 0%, #120D0C 100%)",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "36px",
        fontFamily: VP_FONT,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dot grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff06 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: VP_BRAND.accent,
            }}
          />
          <span
            style={{
              color: VP_BRAND.accent,
              fontSize: "11px",
              letterSpacing: "2px",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {profileHandle ? `@${profileHandle}` : "Case Study"}
          </span>
        </div>
        <span style={{ color: "#333", fontSize: "11px", letterSpacing: "2px" }}>
          VIRAL PARTNERS
        </span>
      </div>
      <div style={{ zIndex: 1 }}>
        <AccentTitle
          text={slide.title || "Twoje Dane"}
          style={{ color: "#fff", fontSize: "30px", fontWeight: 700, lineHeight: 1.1 }}
        />
        {slide.body && (
          <p style={{ color: "#555", fontSize: "13px", marginTop: "12px", lineHeight: 1.5 }}>
            {slide.body}
          </p>
        )}
      </div>
    </div>
  );
}

function DataContent({ slide, slideIndex, totalSlides }: VpSlideProps) {
  const bodyLines = (slide.body || "").split("\n").filter((l) => l.trim());
  const BAR_WIDTHS = [92, 85, 65, 45, 72];
  const BAR_OPACITIES = [1, 0.9, 0.75, 0.6, 0.85];

  return (
    <div
      style={{
        background: "#0D0D10",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "32px",
        fontFamily: VP_FONT,
        gap: "12px",
      }}
    >
      <span
        style={{
          color: "#444",
          fontSize: "10px",
          letterSpacing: "3px",
          textTransform: "uppercase",
        }}
      >
        {pageLabel(slideIndex, totalSlides)}
      </span>
      <AccentTitle
        text={slide.title || "Kluczowa Metryka"}
        style={{
          color: "#fff",
          fontSize: "20px",
          fontWeight: 700,
          margin: "0 0 4px 0",
          lineHeight: 1.2,
        }}
      />
      {bodyLines.length > 1 ? (
        bodyLines.slice(0, 4).map((line, i) => (
          <div
            key={i}
            style={{
              background: "#111116",
              borderRadius: "8px",
              padding: "16px",
              border: "1px solid #1a1a20",
            }}
          >
            <p style={{ color: "#888", fontSize: "13px", lineHeight: 1.5, margin: "0 0 8px 0" }}>
              {line}
            </p>
            <div
              style={{ width: "100%", height: "3px", background: "#1a1a20", borderRadius: "2px" }}
            >
              <div
                style={{
                  width: `${BAR_WIDTHS[i % BAR_WIDTHS.length]}%`,
                  height: "100%",
                  background: VP_BRAND.accent,
                  borderRadius: "2px",
                  opacity: BAR_OPACITIES[i % BAR_OPACITIES.length],
                }}
              />
            </div>
          </div>
        ))
      ) : slide.body ? (
        <div
          style={{
            background: "#111116",
            borderRadius: "8px",
            padding: "16px",
            border: "1px solid #1a1a20",
          }}
        >
          <p
            style={{
              color: "#888",
              fontSize: "14px",
              lineHeight: 1.7,
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
          >
            {slide.body}
          </p>
          <div
            style={{
              width: "100%",
              height: "3px",
              background: "#1a1a20",
              borderRadius: "2px",
              marginTop: "12px",
            }}
          >
            <div
              style={{
                width: "85%",
                height: "100%",
                background: VP_BRAND.accent,
                borderRadius: "2px",
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DataCta({ slide }: VpSlideProps) {
  return (
    <div
      style={{
        background: "#0D0D10",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "36px",
        fontFamily: VP_FONT,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff06 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
      <div
        style={{
          color: VP_BRAND.accent,
          fontSize: "56px",
          fontWeight: 800,
          lineHeight: 1,
          zIndex: 1,
        }}
      >
        ↗
      </div>
      <AccentTitle
        text={slide.title || "Gotowy na zmianę?"}
        style={{
          color: "#fff",
          fontSize: "24px",
          fontWeight: 700,
          margin: "16px 0 12px",
          zIndex: 1,
        }}
      />
      {slide.body && (
        <p
          style={{
            color: "#888",
            fontSize: "14px",
            margin: "0 0 24px",
            zIndex: 1,
            lineHeight: 1.5,
          }}
        >
          {slide.body}
        </p>
      )}
      <div
        style={{
          padding: "12px 32px",
          background: VP_BRAND.accent,
          borderRadius: "6px",
          color: "#fff",
          fontSize: "13px",
          fontWeight: 700,
          letterSpacing: "1px",
          textTransform: "uppercase",
          zIndex: 1,
        }}
      >
        Umów darmowy audyt
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// BOLD CARDS
// Kartowy, modularny. Gradient accenty, badge numeracja.
// ══════════════════════════════════════════════════════

function BoldCover({ slide, profileHandle }: VpSlideProps) {
  return (
    <div
      style={{
        background: "#08080A",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "36px",
        fontFamily: VP_FONT,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Glow effect */}
      <div
        style={{
          position: "absolute",
          bottom: "-60px",
          right: "-60px",
          width: "280px",
          height: "280px",
          background: `radial-gradient(circle, ${VP_BRAND.accentGlow} 0%, transparent 70%)`,
          borderRadius: "50%",
        }}
      />
      <div style={{ zIndex: 1 }}>
        <div
          style={{
            display: "inline-block",
            padding: "6px 14px",
            background: VP_BRAND.accentGlow,
            borderRadius: "20px",
            border: `1px solid ${VP_BRAND.accentBorder}`,
            color: VP_BRAND.accentLight,
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "1px",
            textTransform: "uppercase",
            marginBottom: "16px",
          }}
        >
          {profileHandle ? `@${profileHandle}` : "Viral Partners"}
        </div>
        <span
          style={{
            color: "#333",
            fontSize: "11px",
            position: "absolute",
            top: "36px",
            right: "36px",
            letterSpacing: "2px",
          }}
        >
          VP
        </span>
      </div>
      <div style={{ zIndex: 1 }}>
        <GradientAccentTitle
          text={slide.title || "Twoja Wskazówka"}
          style={{
            color: "#fff",
            fontSize: "34px",
            fontWeight: 800,
            lineHeight: 1.1,
            margin: "0 0 16px 0",
          }}
        />
        {slide.body && <p style={{ color: "#555", fontSize: "13px", margin: 0 }}>{slide.body}</p>}
      </div>
    </div>
  );
}

function BoldContent({ slide, slideIndex }: VpSlideProps) {
  const pointNum = slideIndex;
  const bodyLines = (slide.body || "").split("\n").filter((l) => l.trim());

  let mainBody = slide.body || "";
  let proTip: string | null = null;

  if (bodyLines.length > 1) {
    const lastLine = bodyLines[bodyLines.length - 1].trim();
    const proTipMatch = lastLine.match(/^(?:Pro [Tt]ip:\s*|💡\s*)(.*)/);
    if (proTipMatch) {
      proTip = proTipMatch[1];
      mainBody = bodyLines.slice(0, -1).join("\n");
    }
  }

  return (
    <div
      style={{
        background: "#08080A",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "28px",
        fontFamily: VP_FONT,
        gap: "10px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
        <div
          style={{
            background: `linear-gradient(135deg, ${VP_BRAND.accent}, ${VP_BRAND.accentLight})`,
            color: "#fff",
            width: "28px",
            height: "28px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          {pointNum}
        </div>
        <AccentTitle
          text={slide.title || "Krok do sukcesu"}
          style={{ color: "#fff", fontSize: "18px", fontWeight: 700 }}
        />
      </div>
      <div
        style={{
          background: "#111116",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #1a1a22",
        }}
      >
        <p
          style={{
            color: "#999",
            fontSize: "13px",
            lineHeight: 1.7,
            margin: 0,
            whiteSpace: "pre-wrap",
          }}
        >
          {mainBody}
        </p>
      </div>
      {proTip && (
        <div
          style={{
            background: VP_BRAND.accentGlow,
            borderRadius: "8px",
            padding: "12px 16px",
            border: `1px solid ${VP_BRAND.accentBorder}`,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "14px" }}>💡</span>
          <span style={{ color: VP_BRAND.accentLight, fontSize: "12px", fontWeight: 500 }}>
            {proTip}
          </span>
        </div>
      )}
    </div>
  );
}

function BoldCta({ slide }: VpSlideProps) {
  return (
    <div
      style={{
        background: "#08080A",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "36px",
        fontFamily: VP_FONT,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Center glow */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "300px",
          height: "300px",
          background: `radial-gradient(circle, ${VP_BRAND.accentGlow} 0%, transparent 70%)`,
          borderRadius: "50%",
        }}
      />
      <div style={{ fontSize: "40px", marginBottom: "16px", zIndex: 1 }}>🚀</div>
      <AccentTitle
        text={slide.title || "Dołącz do nas"}
        style={{ color: "#fff", fontSize: "24px", fontWeight: 700, margin: "0 0 8px 0", zIndex: 1 }}
      />
      {slide.body && (
        <p
          style={{
            color: "#666",
            fontSize: "13px",
            margin: "0 0 20px 0",
            zIndex: 1,
            lineHeight: 1.5,
          }}
        >
          {slide.body}
        </p>
      )}
      <div
        style={{
          padding: "12px 28px",
          background: `linear-gradient(135deg, ${VP_BRAND.accent}, ${VP_BRAND.accentLight})`,
          borderRadius: "8px",
          color: "#fff",
          fontSize: "13px",
          fontWeight: 700,
          zIndex: 1,
        }}
      >
        Napisz do nas
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// Dispatcher — style ID → renderer map
// ══════════════════════════════════════════════════════

type VpRenderer = React.FC<VpSlideProps>;

const STYLE_RENDERERS: Record<string, Record<SlideTypeKey, VpRenderer>> = {
  "noir-editorial": { cover: NoirCover, content: NoirContent, cta: NoirCta },
  "data-driven": { cover: DataCover, content: DataContent, cta: DataCta },
  "bold-cards": { cover: BoldCover, content: BoldContent, cta: BoldCta },
};

export function hasVpStyle(styleId: string): boolean {
  return styleId in STYLE_RENDERERS;
}

interface VpSlideDispatcherProps extends VpSlideProps {
  styleId: string;
}

export function VpStyleSlide({ styleId, ...slideProps }: VpSlideDispatcherProps) {
  const renderers = STYLE_RENDERERS[styleId];
  if (!renderers) return null;

  const slideType = getSlideType(slideProps.slide);
  const Renderer = renderers[slideType];
  return <Renderer {...slideProps} />;
}
